/**
 * MobileChatScreen — the chat home on phones (docs: chat is the app's
 * landing page; desktop renders ChatHomeView).
 *
 * A 2-PAGE native scroll-snap pager (Chat | Shortcuts) with a bottom dot
 * indicator, plus a Conversations OVERLAY drawer opened by OVERSCROLL.
 *
 * The pager is pure native scroll (compositor momentum + rubber-band), and we
 * NEVER intercept it — no preventDefault, no scrollLeft writes — so momentum is
 * never killed. To open the drawer we OBSERVE the left-edge overscroll: while a
 * finger is down and the pager is pulled past the chat edge, iOS reports a
 * negative scrollLeft; we map that to the drawer overlay's progress and, on
 * release, snap it open/closed by how far it was pulled (iOS elastic is damped,
 * so the pull is assisted by a release-snap — that's inherent to reading
 * elastic). The drawer itself is a floating 80% overlay (fog scrim + panel),
 * NOT a scroll panel, so it can't over-drag when fully shown.
 *
 * Closing: swipe the open drawer left (follow-the-finger, position-snap), tap
 * the scrim, or pick a session. Mouse/pen (no native elastic): a small pointer
 * drag drives paging, or the drawer when dragged right from the chat edge.
 *
 * File opens navigate to the standalone /file/:id view.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { revealLineRequest, type RevealLineRequest } from '@papan/collab/client'
import { useNavigate } from 'react-router-dom'
import { PanelLeft, Sparkles } from 'lucide-react'
import { ErrorBoundary, ThemedArea, useToast, useDraftState, DraftScopeProvider, WORKSPACE_SCOPE_ATTR, WORKSPACE_LOADED_ATTR, WORKSPACE_SINGLE_ATTR } from '@papan/core-ui/ui'
import { HeroChat, useChatHomeSessions, useOpenConversationFromUrl, ChatShortcuts, ConversationList, SingleFileView, ChatHomePageSwitch, useCanAttach } from '@papan/workspace/ui'
import { filesApi } from '@papan/files/client'
import { useNewProject } from '@papan/files/ui'
import { isMiniappFolder } from '@papan/types'
import { useTenant } from '../hooks/useTenant'

/** Fraction of the drawer width the finger must travel for a release to commit
 *  to open (else it snaps back closed). Low so it opens with a short pull. */
const OPEN_SNAP = 0.18

export function MobileChatScreen() {
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  const navigate = useNavigate()
  const newProject = useNewProject()
  const toast = useToast()
  const [page, setPage] = useState<0 | 1>(0)          // switch: 0 chat, 1 files
  // A file opens IN the files page (the desktop right column's editor), so the
  // Chat | Files switch stays on screen and closing the file lands back on the
  // shortcuts. Navigating to /file/:id instead dropped the switch and returned
  // to the chat page.
  //
  // Shared with the desktop chat home (#897, `chat:openFile`): a phone/desktop
  // layout swap reopens the same file in the other chat, so its editor's unsaved
  // draft has somewhere to come back to.
  const [editorFileId, setEditorFileId] = useDraftState<string | null>('chat:openFile', null)
  // Restored by a swap: show the files page it lives on (the pager starts on chat).
  const restoredFileRef = useRef(editorFileId)
  const [drawerOpen, setDrawerOpen] = useState(false) // committed (resting) open state
  const [drawerActive, setDrawerActive] = useState(false) // fog mounted (open or mid-drag)

  const {
    miniapps, shortcutProjects,
    sessionGroups, busyIds, doneIds,
    selected, activeProject, activeSessionId,
    allProjects, driveAssistant, projectsPending, driveAssistantPending,
    openSession, renameSession, deleteSession, refreshSessionLists,
  } = useChatHomeSessions()

  // The chat-storage page links here with `?ws=&session=`; this deck is
  // reachable from it on a phone exactly as the desktop one is, and the
  // first cut of that fix wired only the desktop deck.
  useOpenConversationFromUrl({ allProjects, driveAssistant, projectsPending, driveAssistantPending, openSession, urlPrefix: prefix, navigate })
  // Attaching follows the SERVER's answer for the active project (the upload route's `mayWriteWorkspace`, #1053),
  // never the tenant role — the same hook the desktop chat home uses.
  const canAttach = useCanAttach(activeProject?.id, activeOrgEmail)

  const scrollerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!restoredFileRef.current) return
    const el = scrollerRef.current
    if (el) el.scrollLeft = el.clientWidth
    setPage(1)
  }, [])
  const panelRef = useRef<HTMLDivElement>(null)
  const fogRef = useRef<HTMLDivElement>(null)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const drawerWidth = () => panelRef.current?.offsetWidth || window.innerWidth * 0.8

  // #249: the line a chat link (`app.py:42`) asked for, kept with the file it is for.
  const [reveal, setReveal] = useState<{ fileId: string; req: RevealLineRequest } | null>(null)
  const openFileInPage = useCallback((fileId: string) => {
    setReveal(null) // handleOpenPath sets its own line after this
    setEditorFileId(fileId)
    const el = scrollerRef.current
    if (el) el.scrollTo({ left: el.clientWidth, behavior: 'smooth' })
  }, [])

  const handleOpenPath = useCallback(async (path: string, at?: { line?: number }) => {
    try {
      const result = await filesApi.resolvePath(path)
      if (!result?.id) {
        toast.error(`File not found: ${path}`)
        return
      }
      // A `.miniapp` runs (mobile AppWindow surface); other folders browse.
      if (result.file.isFolder && isMiniappFolder(result.file.name)) {
        navigate(`${prefix}/app/${result.id}`)
        return
      }
      if (result.file.isFolder) navigate(`${prefix}/folder/${result.id}`)
      else {
        openFileInPage(result.id)
        if (at?.line && at.line > 0) setReveal({ fileId: result.id, req: revealLineRequest(at.line) })
      }
    } catch {
      toast.error(`Could not open: ${path}`)
    }
  }, [navigate, prefix, openFileInPage, toast])

  // Live-readable mirror for the passive touch handlers.
  const drawerOpenRef = useRef(false)
  drawerOpenRef.current = drawerOpen

  /** Paint the drawer at `p` (0 closed, 1 open). Imperative, no re-render. Uses
   *  the `translate` PROPERTY (Tailwind v4's -translate-x-full sets `translate`,
   *  separate from `transform`; transition-transform covers it). */
  const paintDrawer = useCallback((p: number, animate: boolean) => {
    const prog = Math.max(0, Math.min(1, p))
    const panel = panelRef.current
    const fog = fogRef.current
    if (panel) {
      panel.style.transition = animate ? '' : 'none'
      panel.style.translate = `${(prog - 1) * 100}% 0`
    }
    if (fog) {
      fog.style.transition = animate ? '' : 'none'
      fog.style.opacity = String(prog)
    }
  }, [])

  const clearDrawerInline = useCallback(() => {
    const panel = panelRef.current
    const fog = fogRef.current
    if (panel) { panel.style.transition = ''; panel.style.translate = '' }
    if (fog) { fog.style.transition = ''; fog.style.opacity = '' }
  }, [])

  /** Snap to open/closed, then hand control back to the resting CSS classes. */
  const settleDrawer = useCallback((open: boolean) => {
    clearTimeout(settleTimer.current)
    setDrawerActive(true)          // keep fog mounted through the animation
    paintDrawer(open ? 1 : 0, true)
    setDrawerOpen(open)
    drawerOpenRef.current = open
    settleTimer.current = setTimeout(() => {
      if (!open) setDrawerActive(false)
      clearDrawerInline()
    }, 320)
  }, [paintDrawer, clearDrawerInline])

  // ── Scroll observer: dot indicator only. Purely READS scroll. ──
  const onScroll = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const p: 0 | 1 = el.scrollLeft >= (el.clientWidth || 1) / 2 ? 1 : 0
    setPage((prev) => (prev === p ? prev : p))
  }, [])

  // ── Opening drag (touch): PURELY ADDITIVE — passive listeners, NEVER
  //    preventDefault, NEVER write scrollLeft. So native scroll (paging,
  //    momentum, bounce) is completely untouched. We ENGAGE the moment a
  //    rightward pull begins while the pager is on the CHAT half (nearest page =
  //    chat) — NOT only at the exact edge — so a mid-slide pull triggers it. The
  //    drawer is then driven by the TOTAL finger travel since touch-down, so it
  //    opens even while the pager is still settling to chat underneath. Release
  //    snaps by position. ──
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    let s: {
      x: number; y: number; s0: number; startT: number
      axis: 'h' | 'v' | null; engaged: boolean; over: number; lastDx: number; chained: boolean
    } | null = null
    const width = () => panelRef.current?.offsetWidth || window.innerWidth * 0.8
    const nowMs = () => performance.now()

    // Swipe-chaining: a quick rightward PAGING flick (shortcuts→chat) followed
    // by another quick rightward swipe means "keep going past chat" → open the
    // drawer. We remember when the last rightward flick released; if the next
    // swipe starts within CHAIN_MS it is "chained" and drives the drawer from
    // the finger directly (as if already at chat), so momentum stalling the
    // pager is irrelevant — this is the "2 quick swipes from shortcuts" case.
    const FLICK_DX = 24      // min rightward travel to count a swipe as a paging flick
    const FLICK_MS = 500     // ...released within this long
    const CHAIN_MS = 1200    // ...and the next swipe starts within this long
    let lastRightFlickAt = -1e9

    const onStart = (e: TouchEvent) => {
      // `pagerLockedRef`, not `pagerLocked`: this effect is installed once and
      // the flag is declared below it, so a closure would capture a stale
      // value — the same reason `drawerOpenRef` exists here. Without this the
      // TOUCH path ignored the lock that the mouse path honours, and the
      // chained-flick branch sets `reach = 0`, bypassing the distance check
      // that otherwise suppresses a drawer pull on page 1. Two quick right
      // swipes over an open file then slid the drawer across it, from a
      // gesture this surface declares disabled.
      if (drawerOpenRef.current || pagerLockedRef.current) { s = null; return }
      const t = e.touches[0]
      const chained = nowMs() - lastRightFlickAt < CHAIN_MS
      s = { x: t.clientX, y: t.clientY, s0: el.scrollLeft, startT: nowMs(), axis: null, engaged: false, over: 0, lastDx: 0, chained }
    }
    const onMove = (e: TouchEvent) => {
      if (!s) return
      const t = e.touches[0]
      const dx = t.clientX - s.x
      const dy = t.clientY - s.y
      s.lastDx = dx
      if (s.axis === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        s.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
        if (s.axis === 'v') { s = null; return }  // vertical → inner list scroll
      }
      // Drawer progress = finger travel beyond the distance-to-chat. When
      // CHAINED (this is the 2nd of two quick right swipes), that distance is 0
      // — the finger drives the drawer directly. Otherwise it's the touch-down
      // scrollLeft, so a lone swipe from shortcuts pages to chat instead.
      const reach = s.chained ? 0 : s.s0
      s.over = Math.max(0, dx - reach)
      if (s.over > 0 && !s.engaged) { s.engaged = true; setDrawerActive(true) }
      if (s.engaged) paintDrawer(s.over / width(), false)
    }
    const onEnd = () => {
      if (!s) return
      const dur = nowMs() - s.startT
      const rightFlick = s.axis === 'h' && s.lastDx > FLICK_DX && dur < FLICK_MS
      if (s.chained && rightFlick) {
        // The 2nd of two quick right swipes → open by VELOCITY, not distance, so
        // a short flick from a natural finger position works (no reset to the
        // left edge needed). This is the "2 quick swipes from shortcuts" case.
        settleDrawer(true)
      } else if (s.engaged) {
        // Slow deliberate drag → snap by position.
        settleDrawer(s.over / width() >= OPEN_SNAP)
      } else if (rightFlick) {
        // First quick rightward paging flick — arm the chain for a follow-up.
        lastRightFlickAt = nowMs()
      }
      s = null
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: true })
    el.addEventListener('touchend', onEnd, { passive: true })
    el.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [paintDrawer, settleDrawer])

  const goToPage = useCallback((i: 0 | 1) => {
    const el = scrollerRef.current
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }, [])

  // ── Mouse / pen: no native elastic, so drive the overlay directly. A drag
  //    that starts at the chat edge and goes right opens the drawer 1:1; other
  //    drags free-scroll and snap to the nearest page. Fingers bail (native). ──
  const mdrag = useRef<{ x: number; scroll: number; dx: number; mode: 'page' | 'drawer' | null; id: number } | null>(null)
  const suppressClick = useRef(false)

  // Files page with a file open: no swipe or drag paging (see the pager's
  // className). The mouse path writes scrollLeft itself, so it must bail too.
  const pagerLocked = editorFileId !== null && page === 1
  // Mirrored for the touch effect above, which is installed once and cannot
  // close over this value.
  const pagerLockedRef = useRef(pagerLocked)
  pagerLockedRef.current = pagerLocked
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || drawerOpen || pagerLocked) return
    const el = scrollerRef.current
    if (!el) return
    suppressClick.current = false
    mdrag.current = { x: e.clientX, scroll: el.scrollLeft, dx: 0, mode: null, id: e.pointerId }
  }, [drawerOpen, pagerLocked])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = mdrag.current
    const el = scrollerRef.current
    if (!d || !el) return
    d.dx = e.clientX - d.x
    if (d.mode === null) {
      if (Math.abs(d.dx) <= 4) return
      d.mode = d.scroll <= 0 && d.dx > 0 ? 'drawer' : 'page'
      try { el.setPointerCapture(d.id) } catch { /* not capturable */ }
      el.style.cursor = 'grabbing'
      if (d.mode === 'drawer') setDrawerActive(true)
      else el.style.scrollSnapType = 'none'
    }
    if (d.mode === 'drawer') paintDrawer(d.dx / drawerWidth(), false)
    else el.scrollLeft = d.scroll - d.dx
  }, [paintDrawer])

  const endPointer = useCallback(() => {
    const d = mdrag.current
    const el = scrollerRef.current
    if (!d || !el) return
    mdrag.current = null
    el.style.cursor = ''
    try { el.releasePointerCapture(d.id) } catch { /* already released */ }
    if (!d.mode) return
    suppressClick.current = true
    if (d.mode === 'drawer') { settleDrawer(d.dx / drawerWidth() >= OPEN_SNAP); return }
    el.style.scrollSnapType = ''
    const w = el.clientWidth || 1
    const target = Math.max(0, Math.min(1, Math.round(el.scrollLeft / w)))
    el.scrollTo({ left: target * w, behavior: 'smooth' })
  }, [paintDrawer, settleDrawer])

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (!suppressClick.current) return
    suppressClick.current = false
    e.stopPropagation()
    e.preventDefault()
  }, [])

  // ── Closing drag on the open overlay (touch + mouse). A horizontal pull
  //    retracts the panel 1:1; release snaps by position. Vertical falls through
  //    to the conversation list; a horizontal drag swallows its release-click. ──
  const closeDrag = useRef<{ x: number; y: number; dx: number; axis: 'h' | 'v' | null } | null>(null)
  const suppressDrawerClick = useRef(false)

  const onDrawerPointerDown = useCallback((e: React.PointerEvent) => {
    closeDrag.current = { x: e.clientX, y: e.clientY, dx: 0, axis: null }
    suppressDrawerClick.current = false
  }, [])

  const onDrawerPointerMove = useCallback((e: React.PointerEvent) => {
    const d = closeDrag.current
    if (!d) return
    d.dx = e.clientX - d.x
    const dy = e.clientY - d.y
    if (d.axis === null) {
      if (Math.abs(d.dx) < 8 && Math.abs(dy) < 8) return
      d.axis = Math.abs(d.dx) > Math.abs(dy) ? 'h' : 'v'
      if (d.axis === 'v') { closeDrag.current = null; return }
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
    }
    paintDrawer(1 + d.dx / drawerWidth(), false)  // dx ≤ 0 retracts
  }, [paintDrawer])

  const onDrawerPointerEnd = useCallback(() => {
    const d = closeDrag.current
    closeDrag.current = null
    if (!d || d.axis !== 'h') return
    suppressDrawerClick.current = true
    // Sensitive close: a short left pull (~18% of the width) dismisses it.
    settleDrawer(1 + d.dx / drawerWidth() >= 1 - OPEN_SNAP)
  }, [settleDrawer])

  const onDrawerClickCapture = useCallback((e: React.MouseEvent) => {
    if (!suppressDrawerClick.current) return
    suppressDrawerClick.current = false
    e.stopPropagation()
    e.preventDefault()
  }, [])

  return (
    // ws-clear: the shell root paints the violet chat stage on this tab.
    <ThemedArea area="chat" className="ws-clear relative flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* ── 2-page native pager (chat | shortcuts). overscroll-x-contain keeps
          the local rubber-band (so the overscroll is readable) without chaining
          to the page. overflow-y-hidden pins it to the horizontal axis; each
          page owns its own vertical scroller. ──────────────────────────────── */}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClickCapture={onClickCapture}
        // While a file is open on the Files page, swiping is off: a sideways
        // drag inside the editor (selecting text, panning a sheet) must not
        // flip to the chat. The Chat | Files switch below still moves the pager.
        className={`flex-1 min-h-0 flex ${pagerLocked ? 'overflow-x-hidden' : 'overflow-x-auto'} overflow-y-hidden snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      >
        {/* Page 0 — Chat */}
        <div className="hero-chat relative w-full h-full shrink-0 snap-start snap-always flex flex-col px-1 pb-2">
          <button
            onClick={() => settleDrawer(true)}
            aria-label="Conversations"
            className="absolute top-1 left-1 z-10 flex h-9 w-9 items-center justify-center rounded-lg themed-text-muted active:themed-bg-tertiary"
          >
            <PanelLeft size={18} />
          </button>
          {activeProject ? (
            // Boundary, as the desktop twin has. The nearest ancestor one is
            // App.tsx's root, which sits OUTSIDE <BrowserRouter> — so a throw
            // inside the chat (a malformed message part, a bad tool-call entry)
            // unmounted the whole application here, router included, leaving no
            // navigation and a reload that returns to the same crash. On
            // desktop the same throw only replaces the hero.
            <ErrorBoundary>
              <HeroChat
                key={activeProject.id}
                workspaceId={activeProject.id}
                workspaceFolderId={activeProject.folderId}
                orgEmail={activeOrgEmail ?? undefined}
                canWrite={canAttach}
                onOpenBilling={() => navigate(`${prefix}/settings/subscription`)}
                onManageChatStorage={() => navigate(`${prefix}/settings/storage`)}
                sessionId={selected?.project.id === activeProject.id ? activeSessionId : null}
                onSessionCreated={(sid) => {
                  openSession(activeProject, sid)
                  refreshSessionLists(activeProject.id)
                }}
                onTurnComplete={() => refreshSessionLists()}
                onOpenFile={handleOpenPath}
              />
            </ErrorBoundary>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 themed-text-muted px-6 text-center">
              <Sparkles size={32} className="text-purple-400/70" />
              <p className="text-sm">Create a project to start your first conversation.</p>
              <button
                onClick={() => navigate(`${prefix}/projects`)}
                className="ws-tool-btn ws-tool-btn-accent text-sm"
              >
                Open Projects
              </button>
            </div>
          )}
        </div>

        {/* Page 1 — Files: the shortcuts, or the file opened from them or from
            a chat link (embedded editor, like the desktop right column).
            Closing the file shows the shortcuts again. */}
        <div className="w-full h-full shrink-0 snap-start snap-always flex flex-col px-2 pt-1 pb-2">
          {editorFileId ? (
            // Marked with the chat's project (#352) — HeroChat's agent tools are bound to it.
            <div className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0" {...{ [WORKSPACE_SCOPE_ATTR]: activeProject?.id, [WORKSPACE_LOADED_ATTR]: '1', [WORKSPACE_SINGLE_ATTR]: '1' }}>
              <ErrorBoundary>
                <DraftScopeProvider scope="chat">
                <SingleFileView
                  key={editorFileId}
                  tenantId={tenantId}
                  fileId={editorFileId}
                  revealLine={reveal?.fileId === editorFileId ? reveal.req : undefined}
                  onClose={() => setEditorFileId(null)}
                  onSwitchFile={setEditorFileId}
                  // This pager keeps BOTH pages mounted — page 0 is scrolled
                  // off-screen, not unmounted — so a file opened here stays
                  // fully live while the user reads the chat. Without
                  // `deckActive` (which defaults to true) the off-screen editor
                  // answers Ctrl/Cmd+S typed into the composer and keeps its
                  // filename in the browser tab. Same defect the desktop narrow
                  // page had; that fix threaded this prop through
                  // ChatHomeView's renderEditor and missed this surface.
                  deckActive={page === 1}
                  // Phones get the version panel as a 40% overlay rather than a
                  // fixed 288px flex sibling, which on a 390px screen left the
                  // editor around 100px wide. It also enables the slide-down
                  // close animation.
                  mobile
                />
                </DraftScopeProvider>
              </ErrorBoundary>
            </div>
          ) : (
            <ChatShortcuts
              fill
              miniapps={miniapps}
              projects={shortcutProjects}
              onOpenApp={p => navigate(`${prefix}/app/${p.folderId}`)}
              onOpenProject={p => navigate(`${prefix}/workspace/${p.folderId}`)}
              onNewApp={() => newProject.open({ entityKind: 'miniapp', onDone: folderId => navigate(`${prefix}/app/${folderId}`) })}
              onNewProject={() => newProject.open({ onDone: folderId => navigate(`${prefix}/workspace/${folderId}`) })}
              onOpenFile={openFileInPage}
              // Page 1 is the files page: on the chat page their Recent files read is paused.
              active={page === 1}
            />
          )}
        </div>
      </div>

      {/* The Chat | Files switch sits UNDER the pager (not over it), so it
          stays on screen while a file is open on the Files page. */}
      <ChatHomePageSwitch page={page === 0 ? 'chat' : 'files'} onChange={p => goToPage(p === 'chat' ? 0 : 1)} />

      {/* ── Conversations overlay drawer — LEFT, 80%. Floats above the pager;
          opened by overscroll, closed by swipe-left / scrim tap / pick. ──── */}
      <div
        className={`absolute inset-0 z-30 ${drawerOpen ? '' : 'pointer-events-none'}`}
        onPointerDown={onDrawerPointerDown}
        onPointerMove={onDrawerPointerMove}
        onPointerUp={onDrawerPointerEnd}
        onPointerCancel={onDrawerPointerEnd}
        onClickCapture={onDrawerClickCapture}
      >
        {(drawerOpen || drawerActive) && (
          <div
            ref={fogRef}
            className={`mobile-fog mobile-fog-deep absolute inset-0 transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => settleDrawer(false)}
            aria-hidden
          />
        )}
        <div
          ref={panelRef}
          // pan-y: vertical drags scroll the conversation list; horizontal is
          // reserved for the close drag (no pointercancel).
          style={{ touchAction: 'pan-y' }}
          className={`absolute inset-y-0 left-0 flex w-[80%] flex-col transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <div className="ws-card flex-1 flex min-h-0 flex-col" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
            <ConversationList
              sessionGroups={sessionGroups}
              busyIds={busyIds}
              doneIds={doneIds}
              activeProject={activeProject}
              activeSessionId={activeSessionId}
              openSession={openSession}
              renameSession={renameSession}
              deleteSession={deleteSession}
              touch
              // Bring the chat page forward too, as the desktop twin does
              // (ChatHomeView's `setNarrowPage('chat')`). The drawer is
              // reachable from the Files page — that is the documented "2 quick
              // swipes" gesture — so closing it alone left the reader staring
              // at the shortcuts or an open file, with the conversation they
              // had just chosen sitting on the off-screen page and no sign the
              // tap had done anything.
              onSelect={() => { settleDrawer(false); goToPage(0) }}
            />
          </div>
        </div>
      </div>
    </ThemedArea>
  )
}
